import styled from 'styled-components'
import { tokens } from 'styling/tokens'

export const PerformanceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

export const PerformanceCard = styled.div`
  ${(props) => props.theme.content};
  padding: 1.25rem;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  min-height: 14rem;
`

export const PerformanceCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;

  .header-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .title {
    font-size: 15px;
    font-weight: 600;
    color: ${tokens.text.primary};
  }

  .source {
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${tokens.text.secondary};
    font-weight: 500;
  }
`

export const PerformanceStatsRow = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
`

export const PerformanceStatCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .label {
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${tokens.text.secondary};
    font-weight: 500;
  }

  .value-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
  }

  .value {
    font-size: 22px;
    font-weight: 700;
    color: ${tokens.text.primary};
  }

  .change-positive {
    font-size: 12px;
    font-weight: 600;
    color: ${tokens.status.success.fg};
  }

  .change-negative {
    font-size: 12px;
    font-weight: 600;
    color: ${tokens.status.danger.fg};
  }

  .sub-label {
    font-size: 11px;
    color: ${tokens.text.secondary};
  }
`

export const PerformanceFootnote = styled.div`
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
  font-size: 12px;

  .item {
    color: ${tokens.text.primary};

    .num {
      font-weight: 700;
    }

    &.no-show .num {
      color: ${tokens.status.danger.fg};
    }

    &.late .num {
      color: ${tokens.accent.base};
    }

    &.early .num {
      color: ${tokens.text.primary};
    }
  }
`

export const PerformanceDailyBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 3rem;
  width: 100%;
`

export const PerformanceDailyBar = styled.div<{ status?: number }>`
  flex: 1;
  min-width: 4px;
  border-radius: 3px;
  background: ${(props) => {
    switch (props.status) {
      case 1:
        return tokens.status.success.fg
      case 2:
        return tokens.status.danger.fg
      case 3:
        return tokens.accent.base
      case 4:
        return tokens.status.info.fg
      default:
        return tokens.border.hairline
    }
  }};
  height: ${(props) => {
    switch (props.status) {
      case 1:
        return '100%'
      case 2:
        return '40%'
      case 3:
        return '70%'
      case 4:
        return '60%'
      default:
        return '30%'
    }
  }};
`

export const PerformanceDonutContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
`

export const PerformanceLegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 10rem;
`

export const PerformanceLegendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }

  .name {
    color: ${tokens.text.primary};
  }

  .pct {
    color: ${tokens.text.primary};
    font-weight: 600;
  }
`

export const PerformanceMachineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;

  & + & {
    border-top: 1px solid ${tokens.border.hairline};
  }

  .text {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .name {
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.text.primary};
  }

  .role {
    font-size: 12px;
    color: ${tokens.text.secondary};
  }

  .hours {
    font-size: 14px;
    font-weight: 600;
    color: ${tokens.text.primary};
  }
`

export const PerformanceCertificateAlert = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid ${tokens.accent.base};
  background: rgba(255, 153, 0, 0.06);

  .left {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${tokens.text.primary};
    font-size: 13px;

    .name {
      font-weight: 600;
    }

    .expires {
      color: ${tokens.text.secondary};
    }
  }

  .action {
    color: ${tokens.accent.base};
    font-size: 12px;
    font-weight: 600;
    padding: 6px 10px;
    border: 1px solid ${tokens.accent.base};
    border-radius: 8px;
    cursor: pointer;
    background: transparent;
  }
`

export const ListSidebarGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 1.5rem;
  width: 100%;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`

export const ListTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const ListTableHeader = styled.div<{ columns?: string }>`
  display: grid;
  grid-template-columns: ${(props) => props.columns ?? '2fr 1fr 1fr 1fr'};
  padding: 0 1rem 0.75rem 1rem;
  gap: 0.75rem;

  span {
    line-height: 40px;
    font-size: 14px;
    text-transform: uppercase;
    color: ${tokens.text.primary};
    font-weight: 900;
  }
`

export const ListTableRow = styled.div<{ columns?: string }>`
  ${(props) => props.theme.content};
  display: grid;
  grid-template-columns: ${(props) => props.columns ?? '2fr 1fr 1fr 1fr'};
  align-items: center;
  padding: 0.875rem 1rem;
  gap: 0.75rem;
  margin-bottom: 0.625rem;
  border-radius: 10px;
  cursor: pointer;
  transition: outline 0.2s ease;

  &:hover {
    outline: 1px solid ${tokens.accent.base};
  }

  span {
    font-size: 13px;
    color: ${tokens.text.primary};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ellipsis {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }

  .right {
    text-align: right;
  }
`

export const SidePanelCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  .panel-title {
    font-size: 16px;
    font-weight: 700;
    color: ${tokens.text.primary};
  }
`

export const PreviewModalGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`

export const PreviewModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  .label {
    font-size: 12px;
    color: ${tokens.text.primary};
  }

  .value {
    border: 1px solid ${tokens.border.hairline};
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    color: ${tokens.text.primary};
    font-size: 13px;
    min-height: 2.25rem;
    display: flex;
    align-items: center;
  }
`

export const PreviewModalLink = styled.a`
  font-size: 13px;
  color: ${tokens.text.primary};
  text-decoration: underline;
  cursor: pointer;
`
